import React from 'react';
import { RefreshCw } from 'lucide-react';
import { GameWalletSyncStatus } from '../../../../types';
import { useGameWallets } from '../../../../hooks/useApi';
import { useFormatDate } from '../../../../hooks/useFormatDate';
import { cn } from '../../../../utils';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../ui/Table';
import Button from '../../../ui/Button';
import { LoadingSpinner } from '../../../ui/Loading';

interface SyncIssuesTabProps {
  providerId: string;
}

const SyncIssuesTab: React.FC<SyncIssuesTabProps> = ({ providerId }) => {
  const fmt = useFormatDate();
  const { data, isLoading, error, refetch } = useGameWallets({
    page: 1,
    limit: 100,
    gameProviderId: providerId,
    syncStatus: 'failed' as GameWalletSyncStatus,
  });

  const failedWallets = data?.data?.items ?? [];

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-2">
        <p className="text-destructive text-sm">Failed to load sync issues</p>
        <button onClick={() => refetch()} className="text-xs text-primary hover:underline">
          Try again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Sync Issues</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Failed, stuck, or delayed wallet sync records that need review.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
          <RefreshCw className={cn('h-4 w-4 mr-1', isLoading && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Player</TableHead>
              <TableHead>Wallet ID</TableHead>
              <TableHead>Last Error</TableHead>
              <TableHead>API Method</TableHead>
              <TableHead>First Seen</TableHead>
              <TableHead className="text-center">Attempts</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {failedWallets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                  No sync issues found
                </TableCell>
              </TableRow>
            ) : (
              failedWallets.map((wallet) => {
                const latestLog = wallet.errorLogs?.[0];
                return (
                  <TableRow key={wallet.id}>
                    <TableCell>
                      <div className="text-sm font-medium">
                        {wallet.user?.email || wallet.user?.phone || '—'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-xs text-muted-foreground">{wallet.id.slice(0, 12)}…</span>
                    </TableCell>
                    <TableCell>
                      <span className="text-destructive text-xs max-w-[180px] truncate block" title={latestLog?.errorMessage ?? ''}>
                        {latestLog?.errorMessage ?? '—'}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {latestLog?.apiMethod ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {latestLog?.created_at ? fmt(latestLog.created_at) : '—'}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {wallet.errorLogs?.length ?? 0}
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        Failed
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {failedWallets.length > 0 && (
        <p className="text-xs text-muted-foreground">
          {failedWallets.length} wallet{failedWallets.length !== 1 ? 's' : ''} with sync issues.
          Click a wallet in the <span className="font-medium">Wallets</span> tab to retry sync.
        </p>
      )}
    </div>
  );
};

export default SyncIssuesTab;
